import { useMemo } from "react";

import { useComputedColorScheme } from "metabase/ui";
import type { MetabaseEmbeddingThemeV2 } from "metabase/ui/colors";
import { deriveFullMetabaseTheme } from "metabase/ui/colors";

import type { DashboardBranding } from "../theme/branding-types";

type EmbeddingColors = NonNullable<MetabaseEmbeddingThemeV2["colors"]>;

/**
 * Turns a dashboard's stored branding into the `--mb-color-*` custom properties that
 * override the app's global palette for that dashboard's subtree.
 *
 * Returned as a style object for `style={}` rather than CSS text in a <style> element:
 * inline custom properties beat the app's `:root` declarations on specificity, and they
 * need no stylesheet parsing. An earlier version injected a <style> with
 * dangerouslySetInnerHTML; the element mounted with the right CSS but its `.sheet` stayed
 * null, so the browser never applied a single rule. Verified in a real browser.
 *
 * We reuse `deriveFullMetabaseTheme()` rather than emitting only the handful of variables
 * the client set. That function already layers
 * `base theme < whitelabel < embeddingThemeOverride`, strips PROTECTED_COLORS, and --
 * crucially -- re-derives `core-brand`/`core-filter`/`core-summarize` afterwards. Those
 * three back every `color-mix()` stop in `base-colors.ts`, so routing through it means one
 * client color regenerates the entire ramp exactly as the global theme would.
 *
 * Returns `null` when there is nothing to override, so callers render no extra element and
 * the dashboard simply inherits the AGAVE theme.
 */
export function useDashboardBrandingVars(
  branding: DashboardBranding | null | undefined,
): Record<string, string> | null {
  const colorScheme = useComputedColorScheme("light");

  return useMemo(() => {
    if (!branding) {
      return null;
    }

    const colors: EmbeddingColors = {};

    if (branding.brandColor) {
      colors.brand = branding.brandColor;
    }
    if (branding.filterColor) {
      colors.filter = branding.filterColor;
    }
    if (branding.summarizeColor) {
      colors.summarize = branding.summarizeColor;
    }

    const chartColors = branding.chartColors ?? [];

    if (Object.keys(colors).length === 0 && chartColors.length === 0) {
      return null;
    }

    const { colors: derived } = deriveFullMetabaseTheme({
      colorScheme,
      embeddingThemeOverride: { version: 2, colors, chartColors },
    });

    return Object.fromEntries(
      Object.entries(derived)
        .filter(([name, value]) => isSafeCssVar(name, value))
        .map(([name, value]) => [`--mb-color-${name}`, value]),
    );
  }, [branding, colorScheme]);
}

/**
 * Defence in depth for the declarations we inject into a <style> element.
 *
 * The server already validates branding shape, but this hook must stay safe on its own:
 * a dashboard branded before the schema tightened, or a future schema change, should not
 * be able to turn a color into markup. Anything containing characters that could close
 * the style block or start a new rule is dropped rather than escaped -- a missing color
 * falls back to the AGAVE default, which is the safe failure mode.
 */
function isSafeCssVar(name: string, value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[a-z0-9-]+$/i.test(name) &&
    !/[<>{}\n;]/.test(value) &&
    !/(?:@import|url\s*\(|expression\s*\()/i.test(value)
  );
}
